import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Check, Pill, ShieldCheck, FileText, Thermometer, FlaskConical, Building2, Stethoscope, Video, Heart, Scale, Sparkles, Building, HelpCircle } from "lucide-react";
import "@/App.css";

const Dispensing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    setMobileMenuOpen(false);
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const handleMobileNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === 'function') {
      action();
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How are your fees structured?",
      answer: "Our fees are transparent. We do not add mark-ups to medicines, packaging or delivery. Clients pay the actual costs plus a dispensing fee starting from £0.99 per item."
    },
    {
      question: "What medicines can you support?",
      answer: "We support a broad range of medication categories including GSL medicines, pharmacy medicines, prescription-only medicines, controlled drugs where appropriate, temperature-sensitive products and specialist medicines."
    },
    {
      question: "Do you only offer dispensing alongside prescribing?",
      answer: "No. Clients can use dispensing support independently or alongside prescribing services."
    },
    {
      question: "Who is this suitable for?",
      answer: "This service is suitable for clinics, healthcare businesses and providers requiring additional dispensing support."
    }
  ];

  return (
    <div className="dispensing-page" data-testid="dispensing-page">
      {/* Navigation */}
      <nav className="nav" data-testid="navigation">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span className="logo-text">Precision Prescribing</span>
          </div>
          
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <div className="nav-links">
            <button onClick={() => navigate('/clinical-areas')} className="nav-link" data-testid="nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => navigate('/dispensing')} className="nav-link nav-link-active" data-testid="nav-dispensing">Dispensing</button>
            <button onClick={() => navigate('/#how-it-works')} className="nav-link" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => navigate('/#about')} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={scrollToContact} className="nav-link nav-cta" data-testid="nav-contact-cta">Request a Discussion</button>
          </div>
          
          <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav-open' : ''}`} data-testid="mobile-nav">
            <button onClick={() => handleMobileNavClick(() => navigate('/clinical-areas'))} className="mobile-nav-link" data-testid="mobile-nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/dispensing'))} className="mobile-nav-link mobile-nav-link-active" data-testid="mobile-nav-dispensing">Dispensing</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/#how-it-works'))} className="mobile-nav-link" data-testid="mobile-nav-how-it-works">How It Works</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/#about'))} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            <button onClick={() => handleMobileNavClick(scrollToContact)} className="mobile-nav-link mobile-nav-cta" data-testid="mobile-nav-contact-cta">Request a Discussion</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="dispensing-hero" data-testid="dispensing-hero">
        <div className="section-container">
          <h1 className="dispensing-hero-title" data-testid="dispensing-hero-title">
            Dispensing Support for Healthcare Providers
          </h1>
          <p className="dispensing-hero-subtitle" data-testid="dispensing-hero-subtitle">
            Reliable pharmacy dispensing support designed for clinics, healthcare providers and businesses requiring safe, scalable and flexible fulfilment services.
          </p>
          
          <div className="dispensing-hero-features" data-testid="dispensing-hero-features">
            <div className="dispensing-hero-feature">
              <Check size={20} className="feature-check" />
              <span>Dispensing fees from £0.99 per item</span>
            </div>
            <div className="dispensing-hero-feature">
              <Check size={20} className="feature-check" />
              <span>No medicine mark-up</span>
            </div>
            <div className="dispensing-hero-feature">
              <Check size={20} className="feature-check" />
              <span>No packaging mark-up</span>
            </div>
            <div className="dispensing-hero-feature">
              <Check size={20} className="feature-check" />
              <span>No delivery mark-up</span>
            </div>
            <div className="dispensing-hero-feature">
              <Check size={20} className="feature-check" />
              <span>Flexible fulfilment support</span>
            </div>
          </div>
          
          <div className="dispensing-hero-buttons" data-testid="dispensing-hero-buttons">
            <button onClick={scrollToContact} className="btn btn-primary" data-testid="dispensing-cta-discuss">
              Discuss your dispensing needs
            </button>
            <button onClick={scrollToContact} className="btn btn-secondary" data-testid="dispensing-cta-book">
              Book a discovery call
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="dispensing-pricing" data-testid="dispensing-pricing">
        <div className="section-container">
          <h2 className="section-title" data-testid="pricing-title">Simple and transparent pricing</h2>
          <p className="section-description" data-testid="pricing-subtitle">
            Our pricing model is designed to remain straightforward and transparent.
          </p>
          
          <div className="pricing-card" data-testid="pricing-card">
            <div className="pricing-card-header">
              <span className="pricing-amount">Dispensing fees from £0.99 per item</span>
            </div>
            <div className="pricing-card-body">
              <p className="pricing-label">Clients pay:</p>
              <ul className="pricing-list">
                <li><Check size={18} className="pricing-check" /> Actual medication cost</li>
                <li><Check size={18} className="pricing-check" /> Actual packaging cost</li>
                <li><Check size={18} className="pricing-check" /> Actual delivery cost</li>
                <li><Check size={18} className="pricing-check" /> Dispensing fee only</li>
              </ul>
              
              <div className="pricing-note-box">
                <p>We do not add mark-ups to medicines.</p>
                <p>We do not add mark-ups to packaging.</p>
                <p>We do not add mark-ups to delivery.</p>
                <p className="pricing-emphasis">The only service charge applied is the dispensing fee.</p>
              </div>
            </div>
            <div className="pricing-card-footer">
              <p>Dispensing fees start from £0.99 per item and may vary depending on service volume and operational requirements.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Support Section */}
      <section className="dispensing-support" data-testid="dispensing-support">
        <div className="section-container">
          <h2 className="section-title" data-testid="support-title">What we can support</h2>
          <p className="section-description" data-testid="support-subtitle">
            Dispensing support designed around a wide range of healthcare services.
          </p>
          
          <div className="dispensing-support-grid" data-testid="support-grid">
            <div className="dispensing-support-card" data-testid="support-gsl">
              <Pill size={28} className="support-icon" />
              <h3>General Sales List (GSL)</h3>
              <p>Support for routine over-the-counter medicines.</p>
            </div>
            <div className="dispensing-support-card" data-testid="support-p">
              <ShieldCheck size={28} className="support-icon" />
              <h3>Pharmacy Medicines (P)</h3>
              <p>Support for pharmacy medicines requiring pharmacist oversight.</p>
            </div>
            <div className="dispensing-support-card" data-testid="support-pom">
              <FileText size={28} className="support-icon" />
              <h3>Prescription-only Medicines (POM)</h3>
              <p>Support for prescription fulfilment across treatment areas.</p>
            </div>
            <div className="dispensing-support-card" data-testid="support-cd">
              <ShieldCheck size={28} className="support-icon" />
              <h3>Controlled Drugs</h3>
              <p>Support where legally permitted and compliant with regulatory requirements.</p>
            </div>
            <div className="dispensing-support-card" data-testid="support-temp">
              <Thermometer size={28} className="support-icon" />
              <h3>Temperature-sensitive Medicines</h3>
              <p>Cold-chain fulfilment and handling where appropriate.</p>
            </div>
            <div className="dispensing-support-card" data-testid="support-specials">
              <FlaskConical size={28} className="support-icon" />
              <h3>Specials and Specialist Medicines</h3>
              <p>Support for specialist medication requirements.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="dispensing-audience" data-testid="dispensing-audience">
        <div className="section-container">
          <h2 className="section-title" data-testid="audience-title">Who this service is designed for</h2>
          
          <div className="dispensing-audience-grid" data-testid="audience-grid">
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Online clinics</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Private clinics</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Telehealth providers</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Specialist treatment providers</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Weight management services</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Aesthetic clinics</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Healthcare businesses</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Overflow and backup dispensing support</span>
            </div>
            <div className="dispensing-audience-item">
              <Check size={20} className="audience-check" />
              <span>Services without in-house dispensing capability</span>
            </div>
          </div>
        </div>
      </section>

      {/* Flexible Service Model Section */}
      <section className="dispensing-models" data-testid="dispensing-models">
        <div className="section-container">
          <h2 className="section-title" data-testid="models-title">Flexible support built around your service</h2>
          <p className="section-description" data-testid="models-subtitle">
            No two healthcare providers operate in exactly the same way.
          </p>
          
          <div className="dispensing-models-grid" data-testid="models-grid">
            <div className="dispensing-model-card" data-testid="model-prescribing">
              <h3>Prescribing Only</h3>
              <p>Additional pharmacist prescribing support where dispensing already exists.</p>
            </div>
            <div className="dispensing-model-card" data-testid="model-dispensing">
              <h3>Dispensing Only</h3>
              <p>Dedicated dispensing support for businesses requiring fulfilment capacity.</p>
            </div>
            <div className="dispensing-model-card" data-testid="model-combined">
              <h3>Prescribing + Dispensing</h3>
              <p>Combined support where both services are required.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="dispensing-process" data-testid="dispensing-process">
        <div className="section-container">
          <h2 className="section-title" data-testid="process-title">How we support your service</h2>
          
          <div className="dispensing-timeline" data-testid="process-timeline">
            <div className="timeline-item" data-testid="process-step-1">
              <div className="timeline-number">1</div>
              <div className="timeline-content">
                <h3>Understand Your Workflow</h3>
                <p>We learn about your service model and operational requirements.</p>
              </div>
            </div>
            <div className="timeline-item" data-testid="process-step-2">
              <div className="timeline-number">2</div>
              <div className="timeline-content">
                <h3>Align Requirements</h3>
                <p>We define the support required.</p>
              </div>
            </div>
            <div className="timeline-item" data-testid="process-step-3">
              <div className="timeline-number">3</div>
              <div className="timeline-content">
                <h3>Implement Dispensing Support</h3>
                <p>We create a practical workflow around your service.</p>
              </div>
            </div>
            <div className="timeline-item" data-testid="process-step-4">
              <div className="timeline-number">4</div>
              <div className="timeline-content">
                <h3>Ongoing Support</h3>
                <p>We continue supporting service delivery as requirements evolve.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="dispensing-faq" data-testid="dispensing-faq">
        <div className="section-container">
          <h2 className="section-title" data-testid="faq-title">Frequently Asked Questions</h2>
          
          <div className="faq-list" data-testid="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${openFaq === index ? 'faq-item-open' : ''}`}
                data-testid={`faq-item-${index}`}
              >
                <button 
                  className="faq-question" 
                  onClick={() => toggleFaq(index)}
                  data-testid={`faq-question-${index}`}
                >
                  <span>{faq.question}</span>
                  <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer" data-testid={`faq-answer-${index}`}>
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="dispensing-final-cta" data-testid="dispensing-final-cta">
        <div className="section-container">
          <h2 className="final-cta-title" data-testid="final-cta-title">Looking for dispensing support?</h2>
          <p className="final-cta-text" data-testid="final-cta-text">
            Whether you require dispensing support only, prescribing support only or a combined service model, our team can build a solution around your requirements.
          </p>
          <button onClick={scrollToContact} className="btn btn-primary btn-lg" data-testid="final-cta-btn">
            Speak to the team
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand" data-testid="footer-brand">
            <span className="logo-text">Precision Prescribing</span>
            <p className="footer-tagline">Pharmacist-led prescribing capacity for regulated healthcare providers.</p>
          </div>
          <div className="footer-info" data-testid="footer-info">
            <p className="footer-legal-line">Precision Prescribing is a trading name of PHARMKG LTD.</p>
            <p>Company number: 14702569</p>
            <p>ICO registration: ZC107443</p>
            <p>Registered office: 261 High Street, Waltham Cross, EN8 7BE</p>
            <p>Email: <a href="mailto:hello@precisionpx.com">hello@precisionpx.com</a></p>
            <nav className="footer-links" data-testid="footer-links" aria-label="Legal">
              <a href="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/cookie-policy" className="footer-link" data-testid="footer-cookie-link">Cookie Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/terms-of-use" className="footer-link" data-testid="footer-terms-link">Website Terms of Use</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <button
                onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
                className="footer-link"
                data-testid="footer-contact-link"
              >
                Contact
              </button>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dispensing;
